import { toastManager } from '@/app/ui/components/toast';
import {
  analyseEPADocuments,
  deleteEPA,
  getEPADetails,
  updateEPA,
} from '@/app/services/earlyPhaseAssessmentsService';
import { CreateEarlyPhaseAssessmentPayload } from '@/app/types/Dtos/request';
import { mutationKeys, queryKeys } from '@/app/utils/constants';
import { queryOptions, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslations } from 'use-intl';

export const useAnalyseEPADocs = () => {
  const t = useTranslations('general.notifications');

  return useMutation({
    mutationKey: [mutationKeys.ANALYSE_EARLY_PHASE_ASSESSMENT_DOCUMENTS],
    mutationFn: async (earlyPhaseAssessmentId: string) => {
      const res = await analyseEPADocuments(earlyPhaseAssessmentId);
      return res;
    },
    onError: () => {
      toastManager.add({
        type: 'error',
        title: t('errorAnalysisRedirecting'),
      });
    },
  });
};

export const getEPADetailsQuery = (earlyPhaseAssessmentId: string) =>
  queryOptions({
    staleTime: 600 * 1000,
    queryKey: [queryKeys.EARLY_PHASE_ASSESSMENTS_DETAILS, earlyPhaseAssessmentId],
    queryFn: async () => {
      const { data } = await getEPADetails(earlyPhaseAssessmentId);
      return data;
    },
  });

export const useUpdateEPAMutation = () => {
  const t = useTranslations('general.notifications');
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      earlyPhaseAssessmentId,
      ...payload
    }: CreateEarlyPhaseAssessmentPayload & { earlyPhaseAssessmentId: string }) => {
      const res = await updateEPA(earlyPhaseAssessmentId, payload);
      return res;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: [queryKeys.EARLY_PHASE_ASSESSMENTS_DETAILS],
      });
      toastManager.add({
        type: 'success',
        title: t('updatedSuccessfully'),
      });
    },
    onError: () => {
      toastManager.add({
        type: 'error',
        title: t('error'),
      });
    },
  });
};

export const useDeleteEPAMutation = (props: { onSuccess?: () => void }) => {
  const queryClient = useQueryClient();
  const t = useTranslations('general.notifications');

  return useMutation({
    mutationFn: async (earlyPhaseAssessmentId: string) => {
      const res = await deleteEPA(earlyPhaseAssessmentId);
      return res;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: [queryKeys.EVALUATIONS],
      });
      props?.onSuccess?.();
      toastManager.add({
        type: 'success',
        title: t('deletedSuccessfully'),
      });
    },
  });
};
